import $ from "jquery";
import CONFIG from "@bd-core/config";
import { getState, store } from "@bd-stores/store";
import convertToBudgetFormat from "./convertToBudgetFormat";
import { updateAttributes } from "@bd-core/firebase-actions";
import {
    setSpendingBudget,
    updateRoomSpending,
} from "@bd-stores/game-progress";
import bdjs from "@bd-core/bdjs";

export default class Budget {
    constructor() {
        this.elms = {
            budget: ".js-budget",
            budgetAmount: ".js-budget-amount",
            budgetTrigger: ".js-trigger-budget",
            spendingDisplay: ".js-spending-display",
            spendingNewValue: ".js-spending-new-value",
        };
        this.allocatedBudget = CONFIG.game?.allocated_budget || 45000000;
        this.spendingBudget = 0;
        this.loadingTime = 2000; // 2 seconds
    }

    init() {
        const gameProgress = getState().gameProgress;

        if (gameProgress && gameProgress.spendingBudget !== undefined) {
            this.spendingBudget = gameProgress.spendingBudget ?? 0;
        }

        this.updateDisplay();

        this.listeningSpendingBudgetChanged();

        return this;
    }

    listeningSpendingBudgetChanged() {
        bdjs.listen("bd__spendingBudgetChanged", () => {
            const value = getState().gameProgress.spendingBudget ?? 0;
            this.spendingBudget = value;

            setTimeout(() => {
                this.updateDisplay();
            }, this.loadingTime);
        });
    }

    updateDisplay() {
        const formattedAmount = convertToBudgetFormat(
            this.spendingBudget.toString(),
        );
        $(this.elms.spendingDisplay).text(formattedAmount);
    }

    animateDisplay(amount) {
        const formattedAmount = convertToBudgetFormat(amount.toString());
        $(this.elms.spendingNewValue).text(formattedAmount);
    }

    addSpendingBudget(amount) {
        if (amount === 0) return;

        const self = this;
        const currentSpendingBudget = this.getSpendingBudget();
        this.spendingBudget = Number(currentSpendingBudget);
        $(this.elms.budget).addClass("is-updating");

        this.animateDisplay(amount);

        this.spendingBudget += Number(amount);
        this.spendingBudget = Math.max(0, this.spendingBudget);

        self.updateSpendingBudget();

        setTimeout(function () {
            $(self.elms.budget).removeClass("is-updating");
        }, this.loadingTime);
    }

    updateSpendingBudget() {
        setSpendingBudget(this.spendingBudget);
        updateAttributes({ spendingBudget: this.spendingBudget });
    }

    setBudget() {
        this.spendingBudget = getState().gameProgress.spendingBudget ?? 0;

        this.updateDisplay();
    }

    getSpendingBudget() {
        const spendingBudget = getState().gameProgress.spendingBudget ?? 0;
        return spendingBudget;
    }

    resetBudget() {
        this.spendingBudget = 0;

        setTimeout(() => {
            this.updateDisplay();
        }, this.loadingTime);
    }

    show() {
        $(this.elms.budget).removeClass("is-temporary-hidden");
    }

    hide() {
        $(this.elms.budget).addClass("is-temporary-hidden");
    }

    pause() {
        $(this.elms.budget).addClass("is-paused");
    }

    resume() {
        $(this.elms.budget).removeClass("is-paused");
    }

    /**
     * Check if incident budget has already been applied for a specific question
     * @param {string} roomId - The room ID
     * @param {string} questionId - The question ID to check
     * @returns {boolean} - True if incident budget has been applied, false otherwise
     */
    hasIncidentBudgetBeenApplied(roomId, questionId) {
        if (!roomId || !questionId) return false;

        const spending = getState().gameProgress.spending || {};
        const roomSpending = spending[roomId] || { budgetAppliedIncidents: [] };
        const budgetAppliedIncidents = roomSpending["budgetAppliedIncidents"]
            ? Array.isArray(roomSpending["budgetAppliedIncidents"])
                ? roomSpending["budgetAppliedIncidents"]
                : []
            : [];

        return budgetAppliedIncidents.includes(String(questionId));
    }

    /**
     * Check if feedback budget has already been applied for a specific question
     * @param {string} roomId - The room ID
     * @param {string} questionId - The question ID to check
     * @returns {boolean} - True if feedback budget has been applied, false otherwise
     */
    hasFeedbackBudgetBeenApplied(roomId, questionId) {
        if (!roomId || !questionId) return false;

        const spending = getState().gameProgress.spending || {};
        const roomSpending = spending[roomId] || { budgetAppliedFeedbacks: [] };
        const budgetAppliedFeedbacks = roomSpending["budgetAppliedFeedbacks"]
            ? Array.isArray(roomSpending["budgetAppliedFeedbacks"])
                ? roomSpending["budgetAppliedFeedbacks"]
                : []
            : [];

        return budgetAppliedFeedbacks.includes(String(questionId));
    }

    /**
     * Add incident spending budget only if it hasn't been applied for this question yet
     * @param {number} amount - The amount to add
     * @param {string} roomId - The room ID
     * @param {string} questionId - The question ID
     * @returns {boolean} - True if budget was applied, false if already applied
     */
    addIncidentBudgetForQuestion(amount, roomId, questionId) {
        if (!roomId || !questionId) return false;

        if (this.hasIncidentBudgetBeenApplied(roomId, questionId)) return false;

        this.addSpendingBudget(amount);

        store.dispatch(
            updateRoomSpending({
                roomId: roomId,
                budgetAppliedIncident: String(questionId),
            }),
        );

        const updatedSpending = getState().gameProgress.spending || {};
        updateAttributes({ spending: updatedSpending });

        return true;
    }

    /**
     * Add feedback spending budget only if it hasn't been applied for this question yet
     * @param {number} amount - The amount to add
     * @param {string} roomId - The room ID
     * @param {string} questionId - The question ID
     * @returns {boolean} - True if budget was applied, false if already applied
     */
    addFeedbackBudgetForQuestion(amount, roomId, questionId) {
        if (!roomId || !questionId) return false;

        if (this.hasFeedbackBudgetBeenApplied(roomId, questionId)) return false;

        this.addSpendingBudget(amount);

        store.dispatch(
            updateRoomSpending({
                roomId: roomId,
                budgetAppliedFeedback: String(questionId),
            }),
        );

        const updatedSpending = getState().gameProgress.spending || {};
        updateAttributes({ spending: updatedSpending });
    }
}
